import React, { useState } from 'react'
import { X, FileImage, Monitor, Printer, Info, FileText } from 'lucide-react'

const ColorModeDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isDownloading = false,
  downloadType = 'current', // 'current' or 'full'
  format = 'TIF' // 'TIF', 'JPG', 'PNG', 'PDF'
}) => {
  const [selectedColorMode, setSelectedColorMode] = useState('RGB')

  const handleConfirm = () => {
    onConfirm(selectedColorMode)
  }

  const handleClose = () => {
    if (!isDownloading) {
      onClose()
    }
  }

  // Format-specific configurations
  const formatConfig = {
    TIF: {
      icon: FileImage,
      color: 'blue',
      title: 'Export TIF Image',
      supportsCMYK: true,
      description: 'TIF format supports both RGB and CMYK color modes',
      benefits: [
        'Lossless compression - no quality loss',
        'High quality for professional use',
        'Supports both RGB and CMYK color modes',
        'Industry standard for print and design'
      ]
    },
    JPG: {
      icon: FileImage,
      color: 'orange',
      title: 'Export JPG Image',
      supportsCMYK: true,
      description: 'JPG format supports both RGB and CMYK color modes',
      benefits: [
        'Smaller file sizes with good quality',
        'Universal compatibility',
        'Supports both RGB and CMYK color modes',
        'Perfect for photos and web use'
      ]
    },
    PNG: {
      icon: FileImage,
      color: 'green',
      title: 'Export PNG Image',
      supportsCMYK: false,
      description: 'PNG format supports RGB color mode only',
      benefits: [
        'Lossless compression with transparency',
        'Perfect for graphics with transparent backgrounds',
        'RGB color mode for vibrant digital colors',
        'Ideal for web graphics and logos'
      ]
    },
    PDF: {
      icon: FileText,
      color: 'red',
      title: 'Export PDF Document',
      supportsCMYK: true,
      description: 'PDF format supports both RGB and CMYK color modes',
      benefits: [
        'Vector and raster content support',
        'Professional document standard',
        'Supports both RGB and CMYK color modes',
        'Perfect for print-ready documents'
      ]
    }
  }

  const config = formatConfig[format] || formatConfig.TIF
  const IconComponent = config.icon

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <IconComponent className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {config.title}
            </h2>
          </div>
          {!isDownloading && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Choose Color Mode
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {config.description}. Choose the color mode for your export.
            </p>

            {/* Color Mode Options */}
            <div className="space-y-3">
              {/* RGB Option */}
              <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="colorMode"
                  value="RGB"
                  checked={selectedColorMode === 'RGB'}
                  onChange={(e) => setSelectedColorMode(e.target.value)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  disabled={isDownloading}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Monitor className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-gray-900">RGB (Red, Green, Blue)</span>
                  </div>
                  <div className="mt-2 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                    ✓ Recommended for digital use
                  </div>
                </div>
              </label>

              {/* CMYK Option - only show if format supports it */}
              {config.supportsCMYK && (
                <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="colorMode"
                    value="CMYK"
                    checked={selectedColorMode === 'CMYK'}
                    onChange={(e) => setSelectedColorMode(e.target.value)}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    disabled={isDownloading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Printer className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-gray-900">CMYK (Cyan, Magenta, Yellow, Black)</span>
                    </div>
                    <div className="mt-2 text-xs text-purple-700 bg-purple-50 px-2 py-1 rounded">
                      ✓ Recommended for professional printing
                    </div>
                  </div>
                </label>
              )}

              {/* CMY Option - only show if format supports it */}
              {config.supportsCMYK && (
                <label className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="colorMode"
                    value="CMY"
                    checked={selectedColorMode === 'CMY'}
                    onChange={(e) => setSelectedColorMode(e.target.value)}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    disabled={isDownloading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Printer className="w-4 h-4 text-orange-600" />
                      <span className="font-medium text-gray-900">CMY (Cyan, Magenta, Yellow)</span>
                    </div>
                    <div className="mt-2 text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded">
                      ✓ Recommended for artistic printing
                    </div>
                  </div>
                </label>
              )}
            </div>

            {/* Info Box */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">{format} Format Benefits:</p>
                  <ul className="text-xs space-y-1">
                    {config.benefits.map((benefit, index) => (
                      <li key={index}>• {benefit}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="text-sm text-gray-600">
            {downloadType === 'current' ? 'Current page will be exported' : 'All pages will be exported'}
          </div>
          <div className="flex items-center space-x-3">
            {!isDownloading && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={isDownloading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isDownloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Generating {format}...</span>
                </>
              ) : (
                <>
                  <IconComponent className="w-4 h-4" />
                  <span>Export {format} ({selectedColorMode})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ColorModeDialog
